import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  VStack,
  HStack,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Center,
  Box,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronDownIcon, EditIcon, DownloadIcon } from "@chakra-ui/icons";
import { FaPrint, FaPlay } from "react-icons/fa";
import { FiShare2 } from "react-icons/fi";
import { useFindOne, useFindMany, useUser } from "@gadgetinc/react";
import { api } from "../api";
import ScenarioDisplay from "../components/ScenarioDisplay";
import ShareScenarioModal from "../components/ShareScenarioModal";
import RunScenarioModal from "../components/RunScenarioModal";
import { VoiceSimShareModal } from "../components/VoiceSimShareModal";
import { jsPDF } from "jspdf";
import html2canvas from 'html2canvas';
import { buildScenarioDownloadFilename, buildGeneratedScenarioName } from "../lib/scenarioNaming";

export default function ScenarioPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser(api);

  const [{ data: scenario, fetching, error }, refetch] = useFindOne(
    api.clinicalScenario,
    id,
    {
      select: {
        id: true,
        title: true,
        description: { markdown: true },
        levelOfCare: true,
        majorSystemInvolved: true,
        scenarioOutput: true,
        ageRange: true,
        ageBracket: true,
        difficulty: true,
        mechanismNatureOfInjury: true,
        createdAt: true,
        institution: true,
        isPublic: true,
      },
    }
  );

  const [{ data: cohortMemberships }] = useFindMany(api.cohortMembership, {
    filter: {
      userId: { equals: user?.id },
      active: { equals: true },
    },
    select: {
      id: true,
      cohort: {
        id: true,
        name: true,
        displayName: true,
        institution: true,
      },
      primaryRole: true,
    },
    pause: !user?.id,
  });

  // Get instructor cohorts
  const instructorCohorts = cohortMemberships?.filter(
    membership => membership.primaryRole === 'instructor'
  ).map(membership => membership.cohort) || [];

  const [watermarkConfig, setWatermarkConfig] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [isVoiceSimShareOpen, setIsVoiceSimShareOpen] = useState(false);

  React.useEffect(() => {
    const fetchWatermarkConfig = async () => {
      try {
        const wmConfig = await api.getWatermarkConfig();
        setWatermarkConfig(wmConfig);
      } catch (error) {
        console.error('Failed to fetch watermark config:', error);
      }
    };
    fetchWatermarkConfig();
  }, []);

  const handlePrintColored = () => {
    const originalClass = document.body.className;
    document.body.className = 'color-print';
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.className = originalClass;
      }, 500);
    }, 100);
  };

  const handlePrintSimple = () => {
    const originalClass = document.body.className;
    document.body.className = 'simple-print';
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.className = originalClass;
      }, 500);
    }, 100);
  };

  const generateFilename = () => {
    return buildScenarioDownloadFilename(scenario, "pdf", buildGeneratedScenarioName(scenario, scenario?.title));
  };

  const handleDownloadPDF = async () => {
    try {
      toast({
        title: "Generating PDF...",
        status: "info",
        duration: 2000,
      });

      const element = document.getElementById('scenario-content-to-print');
      if (!element) {
        throw new Error('Content not found');
      }

      // Capture the rendered HTML as canvas with optimized settings
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#1a202c'
      });

      // Use JPEG with 70% quality for much smaller file size
      const imgData = canvas.toDataURL('image/jpeg', 0.7);
      const pdf = new jsPDF('p', 'pt', 'letter');

      // Fit image to letter page with margins
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const availableWidth = pageWidth - (2 * margin);
      const availableHeight = pageHeight - (2 * margin);

      const canvasAspect = canvas.width / canvas.height;
      const pageAspect = availableWidth / availableHeight;

      let scaledWidth, scaledHeight, xOffset, yOffset;
      if (canvasAspect > pageAspect) {
        scaledWidth = availableWidth;
        scaledHeight = availableWidth / canvasAspect;
        xOffset = margin;
        yOffset = margin + (availableHeight - scaledHeight) / 2;
      } else {
        scaledHeight = availableHeight;
        scaledWidth = availableHeight * canvasAspect;
        xOffset = margin + (availableWidth - scaledWidth) / 2;
        yOffset = margin;
      }

      pdf.addImage(imgData, 'JPEG', xOffset, yOffset, scaledWidth, scaledHeight, undefined, 'FAST');

      // Add watermark overlay if enabled
      if (watermarkConfig?.enabled) {
        pdf.setFontSize(80);
        pdf.setTextColor(200, 200, 200);
        pdf.setGState(pdf.GState({ opacity: watermarkConfig.overtOpacity || 0.15 }));

        pdf.text(
          watermarkConfig.overtText || 'EDUCATIONAL USE ONLY',
          pageWidth / 2,
          pageHeight / 2,
          {
            align: 'center',
            angle: 45
          }
        );
      }

      pdf.save(generateFilename());

      toast({
        title: "PDF Downloaded",
        description: "Your scenario has been downloaded successfully.",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleRunScenario = () => {
    setIsRunModalOpen(true);
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleBack = () => {
    navigate("/my-scenarios");
  };

  const handleModifyScenario = () => {
    if (!scenario) return;

    const params = new URLSearchParams();
    const output = scenario.scenarioOutput;
    
    // Map levelOfCare to trainingLevel
    if (scenario.levelOfCare) {
      const levelMapping = {
        "EMT_Basic": "EMT-Basic",
        "EMT_Advanced": "EMT-Advanced",
        "Paramedic": "Paramedic",
        "Critical_Care": "Critical Care"
      };
      const trainingLevel = levelMapping[scenario.levelOfCare] || scenario.levelOfCare;
      params.append("trainingLevel", trainingLevel);
    }
    
    // Add difficulty
    if (scenario.difficulty) {
      params.append("difficulty", scenario.difficulty.toString());
    }
    
    // Add primaryBodySystem from majorSystemInvolved
    if (scenario.majorSystemInvolved) {
      params.append("primaryBodySystem", scenario.majorSystemInvolved);
    }
    
    // Add mechanismNatureOfInjury as natureMechanism
    if (scenario.mechanismNatureOfInjury) {
      params.append("natureMechanism", scenario.mechanismNatureOfInjury);
    }
    
    // Extract data from scenarioOutput if available
    if (output) {
      // Patient demographics
      if (output.patientInfo) {
        if (output.patientInfo.age) params.append("patientAge", output.patientInfo.age);
        if (output.patientInfo.sex) params.append("biologicalSex", output.patientInfo.sex);
      }
      
      // Chief complaint
      if (output.chiefComplaint) {
        params.append("chiefComplaint", output.chiefComplaint);
      }
      
      // Secondary complaint
      if (output.secondaryComplaint) {
        params.append("secondaryComplaint", output.secondaryComplaint);
      }
      
      // History fields
      if (output.history) {
        if (output.history.hpi) params.append("hpi", output.history.hpi);
        if (output.history.pmhx) params.append("pmhx", output.history.pmhx);
        if (output.history.medications) params.append("currentMedications", output.history.medications);
        if (output.history.allergies) params.append("allergies", output.history.allergies);
      }
      
      // Primary assessment fields  
      if (output.primaryAssessment) {
        if (output.primaryAssessment.mentalStatus) {
          params.append("levelOfConsciousness", output.primaryAssessment.mentalStatus);
        }
        if (output.primaryAssessment.airway) {
          params.append("airwayStatus", output.primaryAssessment.airway);
        }
        if (output.primaryAssessment.breathing) {
          params.append("breathingStatus", output.primaryAssessment.breathing);
        }
        if (output.primaryAssessment.circulation) {
          params.append("circulatoryStatus", output.primaryAssessment.circulation);
        }
      }
      
      // Secondary assessment fields
      if (output.secondaryAssessment) {
        if (output.secondaryAssessment.heent) params.append("heent", output.secondaryAssessment.heent);
        if (output.secondaryAssessment.respiratory) params.append("respiratory", output.secondaryAssessment.respiratory);
        if (output.secondaryAssessment.cardiovascular) params.append("cardiovascular", output.secondaryAssessment.cardiovascular);
        if (output.secondaryAssessment.abdomen) params.append("gi", output.secondaryAssessment.abdomen);
        if (output.secondaryAssessment.neuro) params.append("nervous", output.secondaryAssessment.neuro);
        if (output.secondaryAssessment.extremities) params.append("musculoskeletal", output.secondaryAssessment.extremities);
      }
      
      // Scene setup fields
      if (output.sceneSetup) {
        if (output.sceneSetup.location) {
          params.append("sceneDescription", output.sceneSetup.location);
        }
        if (output.sceneSetup.safetyIssues) {
          params.append("safetyConsideration", output.sceneSetup.safetyIssues);
        }
      }
    }

    navigate(`/scenario-generator?${params.toString()}`);
  };



  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack mb={6} justify="space-between" className="no-print">
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outline"
          >
            Back to Scenarios
          </Button>
          <HStack spacing={3}>
            <Button
              leftIcon={<Icon as={FaPlay} />}
              colorScheme="green"
              onClick={handleRunScenario}
            >
              Run Scenario
            </Button>
            <Menu>
              <MenuButton 
                as={Button}
                leftIcon={<Icon as={FiShare2} />}
                rightIcon={<ChevronDownIcon />}
                colorScheme="teal"
              >
                Share
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<Icon as={FiShare2} />}
                  onClick={handleShare}
                >
                  Share for Evaluation
                </MenuItem>
                <MenuItem
                  icon={<Icon as={FiShare2} />}
                  onClick={() => setIsVoiceSimShareOpen(true)}
                >
                  Share as Voice Sim
                </MenuItem>
              </MenuList>
            </Menu>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="purple" variant="outline">
                Print Options
              </MenuButton>
              <MenuList>
                <MenuItem icon={<Icon as={FaPrint} />} onClick={handlePrintColored}>
                  Print (Color)
                </MenuItem>
                <MenuItem icon={<Icon as={FaPrint} />} onClick={handlePrintSimple}>
                  Print (Simple)
                </MenuItem>
              </MenuList>
            </Menu>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              variant="outline"
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              onClick={handleModifyScenario}
              variant="outline"
            >
              Modify
            </Button>
          </HStack>
        </HStack>

        {fetching && (
          <Center py={12}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error loading scenario</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {scenario && (
          <>
            {/* Capture area for PDF */}
            <Box id="scenario-content-to-print" position="relative">
              {/* Simple diagonal watermark behind content */}
              {watermarkConfig?.enabled && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  pointerEvents="none"
                  zIndex="998"
                  overflow="hidden"
                  className="watermark-layer"
                >
                  <Text
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%) rotate(-45deg)"
                    fontSize="6xl"
                    fontWeight="bold"
                    color="gray.300"
                    opacity={watermarkConfig.overtOpacity || 0.08}
                    whiteSpace="nowrap"
                    userSelect="none"
                  >
                    {watermarkConfig.overtText || 'EDUCATIONAL USE ONLY'}
                  </Text>
                </Box>
              )}
              
              {/* Scenario content */}
              <Box position="relative" zIndex="999">
                <ScenarioDisplay 
                  scenario={scenario.scenarioOutput} 
                  metadata={{
                    id: scenario.id,
                    title: scenario.title,
                    levelOfCare: scenario.levelOfCare,
                    majorSystemInvolved: scenario.majorSystemInvolved,
                    mechanismNatureOfInjury: scenario.mechanismNatureOfInjury,
                    difficulty: scenario.difficulty,
                    ageRange: scenario.ageRange,
                    ageBracket: scenario.ageBracket,
                    institution: scenario.institution,
                    isPublic: scenario.isPublic,
                    createdAt: scenario.createdAt
                  }} 
                />
              </Box>
              
              {/* Footer with QR codes and attribution - only in PDF */}
              {watermarkConfig?.enabled && (
                <Box
                  className="watermark-footer"
                  mt={8}
                  p={4}
                  borderTop="2px solid"
                  borderColor="gray.300"
                  bg="gray.50"
                >
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" color="gray.600">
                      © {new Date().getFullYear()} {user?.user?.institution || 'PapaMedic AI'} | Educational Use Only
                    </Text>
                    <HStack spacing={4}>
                      <VStack spacing={0}>
                        <Box border="2px solid" borderColor="gray.400" p={1} bg="white">
                          {/* QR code placeholder - actual QR rendered by library */}
                          <Box width="60px" height="60px" bg="gray.200" />
                        </Box>
                        <Text fontSize="xs" mt={1}>Verify</Text>
                      </VStack>
                      <VStack spacing={0}>
                        <Box border="2px solid" borderColor="gray.400" p={1} bg="white">
                          <Box width="60px" height="60px" bg="gray.200" />
                        </Box>
                        <Text fontSize="xs" mt={1}>About</Text>
                      </VStack>
                    </HStack>
                  </HStack>
                </Box>
              )}
            </Box>
            

          </>
        )}

        <style>{`
          /* Always show watermark layer */
          .watermark-layer {
            display: block !important;
            pointer-events: none !important;
          }
          
          /* Hide footer from screen view */
          .watermark-footer {
            display: none;
          }
          
          @media print {
            /* Hide UI controls */
            .no-print,
            .scenario-header,
            .mode-selector,
            .print-mode-selector {
              display: none !important;
            }
            
            /* Show footer in print */
            .watermark-footer {
              display: block !important;
            }
            
            /* Ensure colors print */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Make watermark more visible in print */
            .watermark-layer {
              position: absolute !important;
              display: block !important;
              opacity: 0.15 !important;
              z-index: 998 !important;
            }
            
            /* Ensure boxes print with borders */
            .chakra-card {
              border: 1px solid #E2E8F0 !important;
              page-break-inside: avoid !important;
            }
            
            @page {
              size: letter;
              margin: 0.5in;
            }
          }
        `}</style>
      </VStack>

      <ShareScenarioModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        scenario={scenario}
        cohort={null}
        user={user?.user}
      />

      <RunScenarioModal
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        scenario={scenario}
        instructorCohorts={instructorCohorts}
      />

      {/* Voice Sim Share Modal */}
      <VoiceSimShareModal
        isOpen={isVoiceSimShareOpen}
        onClose={() => setIsVoiceSimShareOpen(false)}
        scenario={scenario}
        instructorCohorts={instructorCohorts}
      />
    </Container>
  );
}
